import React from 'react';
import PropTypes from 'prop-types';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    Tooltip,
} from 'recharts';

import { Spacer } from '@zillow/constellation';

import GraphConstants from 'app/shared/constants/GraphConstants';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import GraphTotalTooltip from 'app/shared/components/Graphs/GraphTotalTooltip';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import subMonths from 'date-fns/sub_months';
import format from 'date-fns/format';

const propTypes = {
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
    showTabs: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
    showTabs: false,
};

const GRAPH = GraphConstants.GRAPHS.LEAD_CLICK;
const TABS = GraphConstants.GRAPH_TABS;

const referralsKey = 'Referrals';
const contactsKey = 'Contacts';

const formatAverage = num => (isNaN(num) ? 0 : Math.round(num));

const LeadClickGraph = ({
    showTabs,
    isLoading,
    data,
}) => {
    const [activeTab, setActiveTab] = React.useState(showTabs ? TABS.AVERAGE : TABS.DEFAULT);

    React.useEffect(() => {
        if (!showTabs) {
            setActiveTab(TABS.DEFAULT);
        }
    }, [showTabs]);

    const handleTabClick = index => setActiveTab(index === 0 ? TABS.AVERAGE : TABS.TOTAL);

    const mappedData = data.map((item, i) => ({
        [referralsKey]: activeTab === TABS.AVERAGE
            ? formatAverage(item.referrals / item.activeListings) : item.referrals,
        [contactsKey]: activeTab === TABS.AVERAGE
            ? formatAverage(item.contacts / item.activeListings) : item.contacts,
        name: format(subMonths(new Date(), 11 - i), 'MMM YYYY'),
    }));

    const configs = GRAPH[activeTab];
    const leadCount = mappedData.map(item => item[contactsKey]).reduce((a, b) => a + b, 0);
    const clickCount = mappedData.map(item => item[referralsKey]).reduce((a, b) => a + b, 0);
    const totalCount = activeTab === TABS.AVERAGE
        ? formatAverage((leadCount + clickCount) / 12) : leadCount + clickCount;
    const title = `${configs.TITLE_PREFIX} ${clickCount ? 'and website referrals' : ''}: ${totalCount || '-'}`;

    const renderGraph = () => (
        <div>
            {!data.length && <GraphEmptyMessage message="No contacts or website referrals available" />}
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={mappedData} key={contactsKey}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <Tooltip content={<GraphTotalTooltip />} />
                    <Legend align="left" verticalAlign="bottom" wrapperStyle={{ marginLeft: 60 }} />
                    <Bar
                        dataKey={contactsKey}
                        maxBarSize={20}
                        fill={GraphConstants.COLORS.zillow.primary}
                    />
                    {clickCount && (
                        <Bar
                            dataKey={referralsKey}
                            maxBarSize={20}
                            fill={GraphConstants.COLORS.zillow.secondary}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <Spacer marginY="md">
            <GraphTitle
                title={title}
                isLoading={isLoading}
                showTabs={showTabs}
                onTabClick={handleTabClick}
                activeIndex={configs.TAB_INDEX}
            />
            {isLoading ? <SpinnerContainer /> : renderGraph()}
        </Spacer>
    );
};

LeadClickGraph.propTypes = propTypes;
LeadClickGraph.defaultProps = defaultProps;

export default LeadClickGraph;
